/**
 * Author: Claude
 * Generation: Agent Battle Royale 3.0 - Map Configuration Adapter
 * Version: v3.0.33
 * Modified: 2025-09-04 Beijing Time
 * 
 * 地图配置适配器 - 从后端配置导入地图参数，避免硬编码
 * 提供前端地图生成系统所需的所有配置参数
 * 支持季节系统和24px瓦片尺寸
 */

// 地形类型枚举 - 匹配后端定义
export enum TerrainType {
  PLAIN = 'PLAIN',
  FOREST = 'FOREST',
  MOUNTAIN = 'MOUNTAIN',
  WATER = 'WATER',
  BUILDING = 'BUILDING'
}

// 地图尺寸接口
export interface MapSize {
  width: number;
  height: number;
}

// 从后端配置常量 (匹配 backend/src/core/config/core/constants.ts)
export const DEFAULT_MAP_SIZE: MapSize = {
  width: 50,
  height: 50
} as const;

export const MAX_MAP_SIZE: MapSize = {
  width: 50,
  height: 50
} as const;

// 地形移动成本 (匹配后端 TERRAIN_MOVEMENT_COSTS)
export const TERRAIN_MOVEMENT_COSTS = {
  [TerrainType.PLAIN]: 1,
  [TerrainType.FOREST]: 1,
  [TerrainType.BUILDING]: 1,
  [TerrainType.MOUNTAIN]: Infinity,
  [TerrainType.WATER]: Infinity
} as const;

// 地形可通行性 (匹配后端 TERRAIN_PASSABLE)
export const TERRAIN_PASSABLE = {
  [TerrainType.PLAIN]: true,
  [TerrainType.FOREST]: true,
  [TerrainType.BUILDING]: true,
  [TerrainType.MOUNTAIN]: false,
  [TerrainType.WATER]: false
} as const;

// 地图生成选项接口
export interface MapGenerationOptions {
  width: number;                      // 地图宽度 (默认50)
  height: number;                     // 地图高度 (默认50)
  seed?: number;                      // 随机种子
  baseDensity?: number;               // 基准地形密度 40% (匹配HTML)
  waterDensity?: number;              // 水域生成密度 (默认使用baseDensity)
  sandDensity?: number;               // 沙地生成密度 (默认使用baseDensity)
  smoothness?: number;                // CA平滑度 (默认3)
  waterRange?: [number, number];      // 水域覆盖率 8%~15%
  mountainRange?: [number, number];   // 山地覆盖率 5%~10%
  forestRange?: [number, number];     // 森林覆盖率 12%~22%
  buildingClusters?: [number, number]; // 建筑聚落数量 2~4个
  housesPerCluster?: [number, number]; // 每簇建筑数量 1~3栋
  nearWaterDist?: [number, number];    // 聚落离水距离 2~5格
  passableRatioMin?: number;           // 可通行连通性最小比例 0.90
}

// 默认地图生成选项
export const DEFAULT_MAP_GENERATION_OPTIONS: MapGenerationOptions = {
  width: DEFAULT_MAP_SIZE.width,
  height: DEFAULT_MAP_SIZE.height,
  baseDensity: 0.40,                  // 40% 基准密度 (匹配HTML默认值)
  smoothness: 3,                      // CA平滑度匹配HTML
  waterRange: [0.08, 0.15],
  mountainRange: [0.05, 0.10],
  forestRange: [0.12, 0.22],
  buildingClusters: [2, 4],
  housesPerCluster: [1, 3],
  nearWaterDist: [2, 5],
  passableRatioMin: 0.90
};

// 地形素材映射
export interface TerrainAssetMapping {
  BASE: {
    PLAIN: string;
    WATER: string;
  };
  OVERLAY: {
    FOREST: string;
    MOUNTAIN: string;  
    BUILDING: string;
  };
}

// 瓦片尺寸配置 (更新为48px以匹配新的48x48/48x53地形素材)
export const TILE_SIZE = 48; // 新的标准瓦片尺寸

// 传统地形素材路径配置 (保留作为fallback)
export const TERRAIN_ASSET_MAPPING: TerrainAssetMapping = {
  BASE: {
    PLAIN: '/assets/FE-picked/plain.png',    // 32x32 平原瓦片 (兼容)
    WATER: '/assets/FE-picked/water.png'     // 32x32 水域瓦片 (兼容)
  },
  OVERLAY: {
    FOREST: '/assets/FE-picked/forest.png',  // 32x32 森林瓦片
    MOUNTAIN: '/assets/FE-picked/mountain.png', // 32x32 山地瓦片
    BUILDING: '/assets/FE-picked/building.png'  // 32x32 建筑瓦片
  }
};

// 地图配置适配器类
export class MapConfigAdapter {
  
  /**
   * 获取默认地图尺寸
   */
  static getDefaultMapSize(): MapSize {
    return { ...DEFAULT_MAP_SIZE };
  }
  
  /**
   * 获取最大地图尺寸
   */
  static getMaxMapSize(): MapSize {
    return { ...MAX_MAP_SIZE };
  }

  /**
   * 获取瓦片尺寸 (24px)
   */
  static getTileSize(): number {
    return TILE_SIZE;
  }
  
  /**
   * 获取地形移动成本
   */
  static getTerrainMovementCost(terrain: TerrainType): number {
    return TERRAIN_MOVEMENT_COSTS[terrain];
  }
  
  /**
   * 检查地形是否可通行
   */
  static isTerrainPassable(terrain: TerrainType): boolean {
    return TERRAIN_PASSABLE[terrain];
  }
  
  /**
   * 获取默认地图生成选项
   */
  static getDefaultGenerationOptions(): MapGenerationOptions {
    return { ...DEFAULT_MAP_GENERATION_OPTIONS };
  }
  
  /**
   * 获取地形素材路径 (传统版本，现已被季节系统取代)
   * @deprecated 使用SeasonalAssetManager代替
   */
  static getTerrainAssetPath(terrain: TerrainType): string {
    switch (terrain) {
      case TerrainType.PLAIN:
        return TERRAIN_ASSET_MAPPING.BASE.PLAIN;
      case TerrainType.WATER:
        return TERRAIN_ASSET_MAPPING.BASE.WATER;
      case TerrainType.FOREST:
        return TERRAIN_ASSET_MAPPING.OVERLAY.FOREST;
      case TerrainType.MOUNTAIN:
        return TERRAIN_ASSET_MAPPING.OVERLAY.MOUNTAIN;
      case TerrainType.BUILDING:
        return TERRAIN_ASSET_MAPPING.OVERLAY.BUILDING;
      default:
        return TERRAIN_ASSET_MAPPING.BASE.PLAIN;
    }
  }
  
  /**
   * 获取所有地形素材映射 (传统版本)
   * @deprecated 使用SeasonalAssetManager代替
   */
  static getAssetMapping(): TerrainAssetMapping {
    return { ...TERRAIN_ASSET_MAPPING };
  }
  
  /**
   * 验证地图生成选项
   */
  static validateGenerationOptions(options: Partial<MapGenerationOptions>): MapGenerationOptions {
    const defaults = DEFAULT_MAP_GENERATION_OPTIONS;

    return {
      width: Math.min(options.width || defaults.width, MAX_MAP_SIZE.width),
      height: Math.min(options.height || defaults.height, MAX_MAP_SIZE.height),
      seed: options.seed,
      baseDensity: options.baseDensity || defaults.baseDensity,
      waterDensity: options.waterDensity || options.baseDensity || defaults.baseDensity,
      sandDensity: options.sandDensity || options.baseDensity || defaults.baseDensity,
      smoothness: options.smoothness || defaults.smoothness,
      waterRange: options.waterRange || defaults.waterRange,
      mountainRange: options.mountainRange || defaults.mountainRange,
      forestRange: options.forestRange || defaults.forestRange,
      buildingClusters: options.buildingClusters || defaults.buildingClusters,
      housesPerCluster: options.housesPerCluster || defaults.housesPerCluster,
      nearWaterDist: options.nearWaterDist || defaults.nearWaterDist,
      passableRatioMin: options.passableRatioMin || defaults.passableRatioMin
    };
  }

  /**
   * 获取史莱姆兼容性信息 (65x64在48x48瓦片上的显示 - 需要2x缩放)
   */
  static getSlimeCompatibilityInfo(): {
    slimeSize: { width: number; height: number };
    tileSize: number;
    anchor: { x: number; y: number };
    tilesSpanned: { width: number; height: number };
    recommendedScale: number;
  } {
    const slimeSize = { width: 65, height: 64 };
    const tileSize = TILE_SIZE;

    return {
      slimeSize,
      tileSize,
      anchor: { x: 0.5, y: 0.9 }, // 底部中心锚点
      tilesSpanned: {
        width: Math.ceil(slimeSize.width / tileSize),  // 约1.4个瓦片宽
        height: Math.ceil(slimeSize.height / tileSize) // 约1.3个瓦片高
      },
      recommendedScale: 2.0 // 2倍缩放使65x64素材显示为130x128，约占2.7个瓦片
    };
  }
}